/**
 * Setting up a UK Direct Debit (Bacs): Stripe only lets a Direct Debit mandate be collected on its own
 * hosted page, so the person is sent there (`start`) and comes back to the same portal page with
 * `?direct_debit=complete&session_id=…` (or `direct_debit=cancelled`). On the way back this keeps the
 * bank account (`complete`, once, even in StrictMode) and tidies the address bar. With the simulated
 * processor the "page" sends them straight back.
 *
 * The page's own state doesn't survive the trip, so what it needs afterwards (`method=bacs_debit`,
 * `make_default`) travels in the return URL.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router";

import { portal } from "@/lib/api";
import { leavePortal } from "@/lib/navigation";
import type { PaymentMethod } from "@/lib/types";

/** Query parameters the return adds, removed once read. */
const RETURN_PARAMS = ["direct_debit", "session_id", "make_default"];

export type DirectDebitReturn =
  | { state: "none" }
  | { state: "completing" }
  | { state: "done"; method: PaymentMethod }
  | { state: "cancelled" }
  | { state: "error"; error: unknown };

export interface DirectDebitEntry {
  /** What came back from the processor's page, if this load is a return. */
  returned: DirectDebitReturn;
  /** Send the person to the processor's page; `keep` are query parameters to come back with. */
  start: (keep: { makeDefault: boolean; params?: Record<string, string> }) => Promise<void>;
  starting: boolean;
  error: unknown;
}

export function useDirectDebit(orgId: string): DirectDebitEntry {
  const qc = useQueryClient();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const outcome = params.get("direct_debit");
  const sessionId = params.get("session_id");
  const makeDefault = params.get("make_default") !== "0";
  const [returned, setReturned] = useState<DirectDebitReturn>(() =>
    outcome === "complete" && sessionId
      ? { state: "completing" }
      : outcome === "cancelled"
        ? { state: "cancelled" }
        : { state: "none" },
  );
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // StrictMode runs effects twice: keep the bank account once.
  const completed = useRef<string | null>(null);

  useEffect(() => {
    if (!outcome) return;
    const tidy = () =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          RETURN_PARAMS.forEach((k) => next.delete(k));
          return next;
        },
        { replace: true },
      );
    if (outcome !== "complete" || !sessionId) {
      tidy();
      return;
    }
    if (completed.current === sessionId) return;
    completed.current = sessionId;
    portal
      .completeDirectDebit(orgId, sessionId, makeDefault)
      .then(
        async (method) => {
          await qc.invalidateQueries({ queryKey: ["payment-methods", orgId] });
          setReturned({ state: "done", method });
        },
        (err: unknown) => setReturned({ state: "error", error: err }),
      )
      .finally(tidy);
  }, [outcome, sessionId, makeDefault, orgId, qc, setParams]);

  const start = useCallback(
    async ({
      makeDefault: asDefault,
      params: extra = {},
    }: {
      makeDefault: boolean;
      params?: Record<string, string>;
    }) => {
      setError(null);
      setStarting(true);
      try {
        // Back to this very page (the router's location: what the address bar shows).
        const back = new URL(`${location.pathname}${location.search}`, window.location.origin);
        RETURN_PARAMS.forEach((k) => back.searchParams.delete(k));
        Object.entries(extra).forEach(([k, v]) => back.searchParams.set(k, v));
        back.searchParams.set("make_default", asDefault ? "1" : "0");
        const { url } = await portal.startDirectDebit(orgId, back.toString());
        leavePortal(url);
      } catch (err) {
        setError(err);
        setStarting(false);
      }
    },
    [orgId, location.pathname, location.search],
  );

  return { returned, start, starting, error };
}
